import React, { KeyboardEvent } from 'react';
import { Select, SelectProps as DropdownProps } from 'antd';
import { SelectPropsEnums } from 'enums/COSelectEnums';
import clsx from 'clsx';
import styles from './COSelect.module.scss';
import {
  Field,
  FieldProps,
  FormikValues,
  useField,
  useFormikContext,
} from 'formik';
import Error from '../Error';
import { ReactComponent as Arrow } from 'assets/icons/arrow.svg';
import { InfoCircleOutlined } from '@ant-design/icons';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { ELEMENT_INPUT, KEYDOWN_ENTER } from 'constants/constants';
import { debounce } from 'lodash';

export interface SelectProps extends DropdownProps {
  multiSelect?: boolean;
  options?: any[];
  handleMultiSelectChange?: (values: (string | number)[]) => void;
  handleSelectChange?: (value: string | number) => void;
  name: string;
  placeholderstring: string;
  disabled?: boolean;
  label?: string;
  value?: string;
  onChange?: (value: any) => void;
  dropdownRender?: any;
  onFocus?: (event: React.FocusEvent<HTMLElement>) => void;
  defaultValue?: any;
  info?: boolean;
  infoClick?: () => void;
  allowClear?: boolean;
  listHeight?: number;
  loading?: boolean;
  popupClassName?: string;
  open?: boolean;
  size?: SizeType;
  showSearch?: boolean;
  onSearch?: (value: string) => void;
  isShowLabel?: boolean;
}

const COSelect: React.FC<SelectProps> = props => {
  const {
    name,
    multiSelect,
    options,
    handleMultiSelectChange,
    placeholderstring,
    label,
    disabled,
    onChange,
    dropdownRender,
    defaultValue,
    onFocus,
    info,
    infoClick,
    allowClear = true,
    listHeight,
    loading,
    onKeyUp,
    onKeyDown,
    showSearch,
    isShowLabel,
    ...remainingProps
  } = props;
  const [showLabel, setshowLabel] = React.useState<boolean>(
    isShowLabel ?? false
  );
  /* helpers provides method to setTouched, and more properties, explicitly. Since meta.touched property works differnt for Select dropdowns.*/
  const [field, meta, helpers] = useField(name);
  const { setFieldValue } = useFormikContext();
  const InputFieldContainerClass = clsx({
    [styles.inputFieldContainer]: true,
    [styles.error]: meta.touched && meta.error,
  });
  const LabelClass = clsx({
    [styles.labelFloat]: true,
    [styles.error]: meta.touched && meta.error,
  });

  const setVal = debounce((event, inputElement) => {
    if (!onKeyUp) return;
    const enteredValue = inputElement?.value?.trim();
    setFieldValue(name, enteredValue ?? null);
    onKeyUp(event);
  }, 150);

  const defaultOnKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!onKeyUp || !showSearch) return;
    const inputElement = event.currentTarget.querySelector(
      ELEMENT_INPUT
    ) as HTMLInputElement;
    if (event.key === KEYDOWN_ENTER) onKeyUp(event);
    else setVal(event, inputElement);
  };

  const defaultOnKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!onKeyDown || !showSearch) return;
    const inputElement = event.currentTarget.querySelector(
      ELEMENT_INPUT
    ) as HTMLInputElement;
    if (event.key === KEYDOWN_ENTER) {
      const enteredValue = inputElement?.value?.trim();
      setFieldValue(name, enteredValue ?? null);
    }
    onKeyDown(event);
  };
  const getPopupContainer = (trigger: HTMLElement): HTMLElement => {
    return (trigger.parentNode as HTMLElement) || trigger;
  };
  if (multiSelect) {
    return (
      <Select
        getPopupContainer={getPopupContainer}
        data-testid={name}
        defaultValue={defaultValue}
        suffixIcon={<Arrow fontSize={44} />}
        mode={SelectPropsEnums.MODE}
        options={options}
        onChange={newValue => handleMultiSelectChange?.(newValue as (string | number)[])}
        placeholder={placeholderstring}
        maxTagCount={SelectPropsEnums.MAX_TAG_COUNT_RESPONSIVE}
        allowClear={allowClear}
        loading={loading}
        {...remainingProps}
      />
    );
  } else {
    return (
      <div className={InputFieldContainerClass}>
        {(showLabel || field.value) && (
          <label htmlFor={name} className={LabelClass}>
            {' '}
            {label}
          </label>
        )}

        <Field name={name}>
          {({ field, form }: FieldProps<FormikValues>) => (
            <>
              <Select
                getPopupContainer={getPopupContainer}
                {...field}
                {...props}
                id={name}
                dropdownMatchSelectWidth={false}
                listHeight={listHeight ?? 120}
                dropdownRender={dropdownRender}
                allowClear={allowClear}
                data-testid={name}
                disabled={disabled}
                showSearch={showSearch}
                suffixIcon={<Arrow fontSize={44} />}
                placeholder={placeholderstring}
                onFocus={(event) => {
                  setshowLabel(true);
                  onFocus && onFocus(event);
                }}
                onChange={newValue => {
                  form.setFieldValue(field.name, newValue ?? null);
                  onChange && onChange(newValue);
                }}
                onBlur={() => helpers.setTouched(true)}
                options={options}
                onKeyUp={defaultOnKeyUp}
                onKeyDown={defaultOnKeyDown}
                defaultValue={defaultValue}
                loading={loading}
                {...remainingProps}
              />
              {info && (
                <div
                  className={styles.infoContainer}
                  onClick={() => {
                    infoClick && infoClick();
                  }}>
                  <p>
                    <InfoCircleOutlined className={styles.infoIcon} />
                  </p>
                </div>
              )}
              {meta.touched && meta.error && <Error message={meta.error} />}
            </>
          )}
        </Field>
      </div>
    );
  }
};

export default COSelect;
